import React from 'react';
import { formatCurrency } from 'utils';
import { grey } from '@mui/material/colors';
import { Delete } from '@mui/icons-material';
import { formatStringToNumber } from 'utils/formatters';
import { DiscountType } from '@trnsact/trnsact-shared-types';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Stack, Button, Box, Typography, IconButton } from '@mui/material';
import { InputField, CurrencyInputField, SelectField, InterestInputField } from 'components/form';
import { EquipmentFormValue } from '../../../../types';
import { FieldsContainer } from '../../../components/FieldsContainer';
import { calcAttachmentTotals, calcDiscount } from '../../../../lib/charges';
import { discountTypes, equipmentAttachment, equipmentAttachmentTypes } from '../../../../constants';

export const Attachments = () => {
  const { control, setValue, getValues } = useFormContext<EquipmentFormValue>();
  const attachments = useWatch({ control, name: 'attachments' });
  const { fields, append, remove } = useFieldArray({ control, name: 'attachments' });

  const discountField = {
    [DiscountType.Flat]: CurrencyInputField,
    [DiscountType.Percent]: InterestInputField,
  };

  const getDiscount = (index: number) => {
    if (!attachments[index]) return 0;

    const { retailPrice, discountType, discountValue } = attachments[index];

    return calcDiscount(retailPrice, discountValue ?? 0, discountType);
  };

  const calcTotals = () => {
    const {
      attachments,
      calcPricing: { customer },
      commonInfo: { retailPrice, saleCost },
    } = getValues();

    const attachmentTotals = calcAttachmentTotals(attachments);

    setValue('calcPricing.retail.attachments', attachmentTotals.retailPrice);
    setValue('calcPricing.retail.total', retailPrice + attachmentTotals.retailPrice);

    setValue('calcPricing.wholesale.attachments', attachmentTotals.wholesaleCost);
    setValue('calcPricing.wholesale.total', saleCost + attachmentTotals.wholesaleCost);

    setValue('calcPricing.customer.attachments', attachmentTotals.customerPriceWithDiscount);
    setValue('calcPricing.customer.total', customer.equipment + attachmentTotals.customerPriceWithDiscount);
  };

  const handleAddAttachment = () => {
    append({ ...equipmentAttachment });
  };

  const handleRemoveAttachment = (index: number) => {
    remove(index);
    calcTotals();
  };

  return (
    <Stack gap="1rem">
      {!!fields.length &&
        fields.map(({ id }, index) => {
          const DiscountFieldComponent = discountField[attachments[index]?.discountType];

          return (
            <Stack
              key={id}
              sx={{
                gap: '1rem',
                padding: '1rem',
                borderRadius: '0.5rem',
                background: grey['50'],
                border: `1px solid ${grey[300]}`,
              }}
            >
              <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: '0.5rem' }}>
                <IconButton
                  size="small"
                  color="error"
                  className="icon-btn-bordered"
                  onClick={() => {
                    handleRemoveAttachment(index);
                  }}
                >
                  <Delete fontSize="small" color="error" />
                </IconButton>

                <Typography component="span" variant="subtitle2">
                  Attachment #{index + 1}
                </Typography>
              </Stack>

              <FieldsContainer key={id} columns={5}>
                <InputField
                  control={control}
                  label="Qty. Per Asset"
                  formatter={formatStringToNumber}
                  name={`attachments.${index}.quantityPerAsset`}
                  inputProps={{ type: 'number', variant: 'outlined' }}
                />

                <SelectField
                  label="Type"
                  control={control}
                  variant="outlined"
                  name={`attachments.${index}.type`}
                  options={equipmentAttachmentTypes}
                />

                <InputField
                  control={control}
                  label="Manufacturer"
                  inputProps={{ variant: 'outlined' }}
                  name={`attachments.${index}.manufacturer`}
                />

                <InputField
                  label="Model"
                  control={control}
                  name={`attachments.${index}.model`}
                  inputProps={{ variant: 'outlined' }}
                />

                <InputField
                  label="Category"
                  control={control}
                  name={`attachments.${index}.category`}
                  inputProps={{ disabled: true, variant: 'outlined' }}
                />
              </FieldsContainer>

              <Stack gap="1rem">
                <Typography variant="subtitle2" component="span">
                  Pricing
                </Typography>

                <FieldsContainer columns={4}>
                  <CurrencyInputField
                    control={control}
                    label="Wholesale Cost"
                    name={`attachments.${index}.unitCost`}
                    textFieldProps={{ variant: 'outlined' }}
                    customOnChange={(nextValue, name) => {
                      setValue(name, nextValue);
                      calcTotals();
                    }}
                  />

                  <CurrencyInputField
                    control={control}
                    label="Retail Price"
                    name={`attachments.${index}.retailPrice`}
                    textFieldProps={{ variant: 'outlined' }}
                    customOnChange={(nextValue, name) => {
                      setValue(name, nextValue);
                      calcTotals();
                    }}
                  />

                  <SelectField
                    control={control}
                    variant="outlined"
                    label="Discount Type"
                    options={discountTypes}
                    name={`attachments.${index}.discountType`}
                    extraAction={calcTotals}
                  />

                  {DiscountFieldComponent && (
                    <DiscountFieldComponent
                      control={control}
                      label="Discount"
                      name={`attachments.${index}.discountValue`}
                      textFieldProps={{ variant: 'outlined' }}
                      customOnChange={(nextValue, name) => {
                        setValue(name, nextValue);
                        calcTotals();
                      }}
                    />
                  )}
                </FieldsContainer>

                <Typography variant="subtitle2" component="span">
                  Customer Price: {formatCurrency(getDiscount(index), true)}
                </Typography>
              </Stack>
            </Stack>
          );
        })}

      <Box>
        <Button size="small" onClick={handleAddAttachment}>
          Add attachments
        </Button>
      </Box>
    </Stack>
  );
};
